import { zodResolver } from '@hookform/resolvers/zod';
import {
  InvoiceSummaryResp,
  InvoiceVoidReasonCodeEnum,
  VoidInvoiceErrorResponse,
  VoidInvoiceFormInput,
  VoidInvoiceRequest,
  VoidInvoiceRequestSchema,
  VoidInvoiceResponse,
} from '@monetize/types/app';
import { isBillingPlatformApiErrorResponse } from '@monetize/types/common';
import {
  formatCurrency,
  formatInteger,
  toDateShort,
} from '@monetize/utils/core';
import { isAxiosError } from 'axios';
import { useMemo, useState } from 'react';
import { Controller, FieldErrors, useForm } from 'react-hook-form';
import { MdCheckCircle, MdError } from 'react-icons/md';
import { useVoidInvoices } from '../../../../api/invoiceService';
import {
  MAlert,
  MCenterModal,
  MFlex,
  MFormField,
  MIcon,
  MList,
  MListItem,
  MSpinner,
  MStack,
  MText,
} from '../../../../components/Monetize';
import { ItemWithLinkInNewWindow } from '../../../../components/Monetize/ItemWithLinkInNewWindow';
import { MCustomSelect } from '../../../../components/Monetize/MCustomSelect/MCustomSelect';
import { MTextAreaCharacterLimited } from '../../../../components/Monetize/MTextAreaCharacterLimited';
import { INVOICE_VOID_REASON_CODE_OPTIONS } from '../../../../constants/invoice';
import {
  getAccountDetailRoute,
  getInvoiceDetailRoute,
} from '../../../../constants/routes';
import { logger } from '../../../../services/logger';
import { useToast } from '../../../../services/toast';
import { useMassInvoiceAction } from '../useMassInvoiceAction';
import { MassVoidModalFooter } from './MassVoidModalFooter';

interface MassVoidModalProps {
  selectedRows: InvoiceSummaryResp[];
  /**
   * Used to determine correct order of selected rows
   */
  visibleRows: InvoiceSummaryResp[];
  onClose: () => void;
}

export const MassVoidModal = ({
  selectedRows: selectedRowsUnordered,
  visibleRows,
  onClose,
}: MassVoidModalProps) => {
  const [result, setResult] = useState<VoidInvoiceResponse | null>(null);
  const { addToast } = useToast();

  const { mutate: doVoidInvoices, isPending: isLoading } = useVoidInvoices({
    onSuccess: (response) => {
      setResult(response);
      const failureCount = response.errors.length;
      if (failureCount === 0) {
        addToast({
          summary: 'Success',
          detail: response.message,
          severity: 'success',
        });
      } else if (response.voidedInvoices.length > 0) {
        addToast({
          summary: 'Partial Success',
          detail: response.message,
          severity: 'warning',
        });
      } else {
        addToast({
          summary: 'Voiding Failed',
          detail: response.message,
          severity: 'error',
        });
      }
    },
    onError: (error) => {
      // When none of the requested invoices are voidable, the backend
      // rejects the whole request with a 400 carrying the per-invoice
      // errors in its context instead of returning them in a 200 response.
      const response = isAxiosError(error) ? error.response?.data : error;
      if (
        isBillingPlatformApiErrorResponse(response) &&
        Array.isArray(response.error.context?.errors)
      ) {
        setResult({
          message: response.error.details,
          voidedInvoices: [],
          errors: response.error.context.errors as VoidInvoiceErrorResponse[],
        });
      }
    },
    meta: { showErrorToast: true },
  });

  const { initiateSubmit, hasSubmitted, rows } = useMassInvoiceAction({
    selectedRows: selectedRowsUnordered,
    visibleRows,
    isLoading,
    verbiage: {
      successSingularMessage: 'Voided 1 invoice',
      successSuffix: 'voided',
      failureSingularMessage: 'Failed to void invoice',
      failureSuffix: 'failed to void',
      partialFailureSuffix: 'failed to void',
    },
  });

  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
  } = useForm<VoidInvoiceFormInput, unknown, VoidInvoiceRequest>({
    resolver: zodResolver(VoidInvoiceRequestSchema),
    defaultValues: {
      invoiceIds: rows.map((row) => row.id),
      internalComment: '',
    },
  });

  const reasonCode = watch('reasonCode');
  const internalComment = watch('internalComment');

  const isSubmitDisabled =
    !reasonCode ||
    (reasonCode === InvoiceVoidReasonCodeEnum.OTHER &&
      !internalComment?.trim());

  function onSubmit(data: VoidInvoiceRequest) {
    void initiateSubmit({
      data,
      callback: () => {
        doVoidInvoices(data);
      },
    });
  }

  function onError(error: FieldErrors<VoidInvoiceFormInput>) {
    logger.error('onSubmit', error);
  }

  const voidedInvoiceIds = useMemo(
    () => new Set(result?.voidedInvoices.map((invoice) => invoice.id) ?? []),
    [result],
  );
  const errorMessagesByInvoiceId = useMemo(
    () =>
      new Map(
        result?.errors.map((error) => [error.invoiceId, error.message]) ?? [],
      ),
    [result],
  );

  // Once submitted, surface errored invoices first so they're easy to spot
  const displayRows = useMemo(() => {
    if (!hasSubmitted || !result) {
      return rows;
    }
    return [...rows].sort((a, b) => {
      const aHasError = errorMessagesByInvoiceId.has(a.id) ? 0 : 1;
      const bHasError = errorMessagesByInvoiceId.has(b.id) ? 0 : 1;
      return aHasError - bHasError;
    });
  }, [rows, hasSubmitted, result, errorMessagesByInvoiceId]);

  return (
    <MCenterModal
      blockScrollOnMount
      closeOnEsc={false}
      closeOnOverlayClick={false}
      showCloseButton={!isLoading}
      modalTitle="Void Invoices"
      size="2xl"
      renderFooter={() => (
        <MassVoidModalFooter
          total={rows.length}
          success={result?.voidedInvoices.length}
          failure={result?.errors.length}
          isLoading={isLoading}
          hasSubmitted={hasSubmitted}
          isSubmitDisabled={isSubmitDisabled}
          onCancel={onClose}
        />
      )}
      isOpen
      onClose={onClose}
    >
      <form
        id="mass-void-form"
        onSubmit={handleSubmit(onSubmit, onError)}
        noValidate
      >
        <MAlert type="warning" mt={1}>
          Voiding an invoice cannot be undone. Once an invoice has been voided,
          you may generate a new invoice for this period if needed.
        </MAlert>

        <MFormField
          isRequired
          label="Reason Code"
          error={errors.reasonCode}
          mb={3}
        >
          <Controller
            name="reasonCode"
            control={control}
            render={({ field }) => (
              <MCustomSelect
                {...field}
                items={INVOICE_VOID_REASON_CODE_OPTIONS}
                placeholder="Select a reason"
                isDisabled={isLoading || hasSubmitted}
              />
            )}
          />
        </MFormField>

        <MFormField
          isRequired={reasonCode === InvoiceVoidReasonCodeEnum.OTHER}
          label="Internal Comment"
          tooltip="Include any additional explanation for why this invoice was voided. These comments are not exposed to end customers."
          error={errors.internalComment}
          mb={3}
        >
          <Controller
            name="internalComment"
            control={control}
            render={({ field }) => (
              <MTextAreaCharacterLimited
                field={field}
                maxLength={500}
                placeholder="Describe the reason for voiding..."
                isDisabled={isLoading || hasSubmitted}
              />
            )}
          />
        </MFormField>

        <MText fontWeight="600" mb={2}>
          {`Invoices (${formatInteger(rows.length)})`}
        </MText>
        <MList
          variant="plain"
          maxH="40vh"
          overflowY="auto"
          border="1px"
          borderColor="border.subtle"
          borderRadius="md"
        >
          {displayRows.map((row, i) => {
            const isPending = hasSubmitted && isLoading && !result;
            const isVoided = result ? voidedInvoiceIds.has(row.id) : undefined;
            const errorMessage = result
              ? errorMessagesByInvoiceId.get(row.id)
              : undefined;
            return (
              <MListItem
                key={row.id}
                borderBottom={i !== displayRows.length - 1 ? '1px' : '0'}
                borderBottomColor="border.subtle"
                py="2"
                px="3"
              >
                <MFlex alignItems="center" gap={2}>
                  {isPending && <MSpinner size="xs" />}
                  {!isPending && isVoided !== undefined && (
                    <MIcon
                      as={errorMessage ? MdError : MdCheckCircle}
                      color={errorMessage ? 'tRed.base' : 'tGreen.approval'}
                      w="4"
                      h="4"
                    />
                  )}
                  <MStack gap="0" flex="1">
                    <MFlex justify="space-between" align="baseline" gap={2}>
                      <ItemWithLinkInNewWindow
                        url={getInvoiceDetailRoute(row.id)}
                      >
                        <MText fontWeight="bold">
                          {row.invoiceNumber || row.id}
                        </MText>
                      </ItemWithLinkInNewWindow>
                      <MText fontWeight="600" whiteSpace="nowrap">
                        {formatCurrency(row.amount, {
                          currency: row.currency,
                        })}
                      </MText>
                    </MFlex>
                    <MFlex justify="space-between" align="baseline" gap={2}>
                      <ItemWithLinkInNewWindow
                        url={getAccountDetailRoute(row.account.id)}
                      >
                        <MText fontSize="sm">{row.account.accountName}</MText>
                      </ItemWithLinkInNewWindow>
                      <MText fontSize="xs" color="fg.muted" whiteSpace="nowrap">
                        Due {toDateShort(row.dueDate)}
                      </MText>
                    </MFlex>
                    {errorMessage && (
                      <MText fontSize="xs" color="tRed.base">
                        {errorMessage}
                      </MText>
                    )}
                  </MStack>
                </MFlex>
              </MListItem>
            );
          })}
        </MList>
      </form>
    </MCenterModal>
  );
};
